import { Box3, Group, Mesh, MeshStandardMaterial, Object3D, SkinnedMesh, Vector3 } from "three";
import { ICollidable } from "../../../../../../three/physics/ICollidable";
import { IDamageable } from "../../IDamageable";
import { ISpawnable } from "../../spawner/ISpawnable";
import { Event } from "event-system";
import { MoleAnimationController, MoleAnimationState } from "./MoleAnimationController";
import { Projectile } from "../../projectiles/abstract/Projectile";
import { Mine } from "../mines/Mine";
import { IMoleState } from "./states/IMoleState";
import { PhysicsLayers } from "../../../../../../PhysicsLayers";
import { Ticker } from "pixi.js";
import { clone } from "three/examples/jsm/utils/SkeletonUtils.js";
import { Physics } from "../../../../../../three/physics/Physics";
import { GameResourcesPackage } from "../../../../resources/GameResourcesPackage";
import { AudioController, Sfx } from "../../../AudioController";
import { MoleProjectile } from "../../projectiles/MoleProjectile";
import { IProjectileContainer } from "../IProjectileContainer";
import { IMinable } from "../mines/IMinable";
import { HatType } from "../../../../models/data/HatType";
import { Character } from "../../character/Character";
import { BatchedRenderer } from "three.quarks";
import { StunningParticle } from "../particles/StunningParticle";
import { GameController } from "../../../../controllers/GameController";
import { MaterialUtils } from "../../../../../../three/MaterialsUtils";

export abstract class BaseMole extends Object3D implements ICollidable, IDamageable, ISpawnable, IMinable {
    public readonly isDamageable: boolean = true;
    public readonly isMinable: boolean = true;
    public readonly onCanBeDestroyed: Event<ISpawnable>;

    protected readonly animationController: MoleAnimationController;
    protected readonly moleObject: Object3D;
    protected readonly moleHead: Object3D;
    protected readonly hatPosition: Object3D;
    protected readonly throwPosition: Object3D;
    protected readonly moleMesh: SkinnedMesh;
    protected stunEffect: StunningParticle;
    protected readonly _boundingBox: Box3;
    protected hat?: Mesh;
    protected ice: Group;
    protected weaponObject: Projectile;
    protected _mine: Mine;
    public get mine(): Mine {
        return this._mine;
    }
    public set mine(value: Mine) {
        this._mine = value;
        if (value != null) {
            this._mine.exploded.once(this.onMineExploded, this);
            this._mine.position.set(0, 6, 0);
            this.add(this._mine);
            // Need to do it for Physics to work properly
            this._mine.updateMatrixWorld(true);
        }
    }
    protected currentState: IMoleState;
    public get state(): IMoleState {
        return this.currentState;
    }
    protected freezedTimeMs: number;

    public get head(): Object3D {
        return this.moleHead;
    }

    public get boundingBox(): Box3 {
        const size = this._boundingBox.copy(this.moleMesh.boundingBox).getSize(new Vector3());
        return this._boundingBox.copy(this.moleMesh.boundingBox).expandByVector(size.clone().multiplyScalar(0.1)).applyMatrix4(this.matrixWorld);
    }

    public get collidesWithLayer(): number {
        return this.alive ? 1 << PhysicsLayers.Layer2 : 0;
    }

    public get alive(): boolean {
        return this.health > 0;
    }

    public get freezed(): number {
        return this.freezedTimeMs;
    }

    public get mined(): boolean {
        return this._mine != null;
    }

    public constructor(
        public readonly fieldIndex: number,
        protected readonly gameResources: GameResourcesPackage,
        protected readonly projectileContainer: IProjectileContainer,
        protected readonly particlesRenderer: BatchedRenderer,
        protected readonly character: Character,
        protected readonly audioController: AudioController,
        protected readonly gameController: GameController,
        protected health: number = 100,
        protected attackDelay = 2000
    ) {
        super();

        this.onCanBeDestroyed = new Event();

        const isOldModel = Math.random() < 0.5;
        this.moleObject = clone(isOldModel ? gameResources.assets_game_Mole_glb.scene.children[0] : gameResources.assets_game_Mole_old_glb.scene.children[0]);

        this.hatPosition = this.moleObject.getObjectByName("Hat_position");
        this.hat = this.getHat();
        if (this.hat != null) {
            this.hatPosition.add(this.hat);
            this.hat.updateMatrixWorld(true);
        }
        this.moleHead = this.moleObject.getObjectByName("Head");
        this.throwPosition = this.moleObject.getObjectByName("ThrowingPosition");

        this._boundingBox = new Box3();
        this.moleMesh = this.moleObject.children[0] as SkinnedMesh;
        this.moleMesh.computeBoundingBox();
        this.moleMesh.computeBoundingSphere();
        this.animationController = new MoleAnimationController(this.moleMesh, gameResources.assets_game_Mole_glb.animations);
        this.add(this.moleObject);
        Ticker.shared.add(this.onTick, this);
        Physics.add(this);
        this.addEventListener("added", this.onAddedToStage);
    }

    protected onAddedToStage(): void {
        this.lookAtCharacter();
    }

    public lookAtCharacter(): void {
        this.updateMatrixWorld(true);
        const charPositionW = this.character.getWorldPosition(new Vector3());
        this.lookAt(charPositionW);
    }

    public destroy(): void {
        Ticker.shared.remove(this.onTick, this);
        Physics.remove(this);
        this.removeFromParent();
        this.removeEventListener("added", this.onAddedToStage);
        this.stunEffect?.dispose();
        this.animationController.dispose();
    }

    public setupMineOnTop(mine: Mine): void {
        this._mine = mine;
        this.head.add(mine);
        let topY: number = 0.3;
        if (this.hat != null) {
            const bounds = this.hat.geometry.boundingBox.clone().applyMatrix4(this.matrix);
            topY = bounds.max.y - bounds.min.y;
        }
        this._mine.position.set(0, topY, 0);
        this._mine.exploded.once(this.onMineExploded, this);
    }

    public clearMine(): void {

    }

    public throw(): void {
        if (!this.character.alive) {
            return;
        }
        const targetWorldPosition = this.character.bodyPosition.clone();
        this.weaponObject.throwToPoint(targetWorldPosition);
    }

    public takeDamage(amount: number): void {
        this.audioController.playSfx(Sfx.Hit);
        this.health = Math.max(this.health - amount, 0);
        if (this.alive) {
            this.dropHat();
            this.animationController.transitToState(MoleAnimationState.KickToTheGroin);
        } else {
            this.stunEffect = new StunningParticle(this.gameResources, this.particlesRenderer);
            this.hatPosition.add(this.stunEffect);
            this.gameController.handleMoleDie(this.fieldIndex);
        }
    }

    public equipProjectile(): void {
        this.weaponObject = new MoleProjectile(this.projectileContainer, clone(this.gameResources.assets_game_Weapons_glb.scene.children[3]) as Mesh, 0.2, 5);
        this.throwPosition.add(this.weaponObject);
        this.weaponObject.updateMatrixWorld();
    }

    public freeze(freezeTimeMS: number): void {
        this.freezedTimeMs = freezeTimeMS;
        if (this.ice != null) {
            return;
        }

        this.ice = clone(this.gameResources.assets_game_IceCube_glb.scene.getObjectByName("Ice_Cube")) as Group;
        this.ice.traverse((obj) => {
            if ((obj as Mesh).isMesh) {
                const mesh = obj as Mesh;
                if (mesh.material instanceof MeshStandardMaterial) {
                    mesh.material = MaterialUtils.convertToBasicMaterial(mesh.material);
                }
            }
        });
        this.ice.rotation.y = Math.floor(Math.random() * 4) * (Math.PI / 2);
        this.add(this.ice);
        this.audioController.playSfx(Sfx.Freeze);
    }

    public unfreeze(): void {
        this.freezedTimeMs = 0;
        if (this.ice != null) {
            this.remove(this.ice);
            this.ice = null;
        }
    }

    public onMineExploded(): void {
        this._mine = null;
    }

    protected switchState(nextState: IMoleState): void {
        this.currentState?.exit();
        const previousState = this.currentState;
        this.currentState = nextState;
        this.currentState.enter(previousState);
    }

    protected onTick(): void {
        const nextState = this.currentState.process(Ticker.shared.deltaMS);
        if (nextState != this.currentState) {
            if (nextState != null) {
                this.switchState(nextState);
            } else {
                this.onCanBeDestroyed.emit(this);
            }
        }
    }

    protected getHat(): Mesh {
        const hatType = this.resolveHatType();
        if (hatType == null) {
            return;
        }
        const hat = this.gameResources.assets_game_Hat_glb.scene.getObjectByName(hatType);
        if (hat == null) {
            throw new Error(`Unable to get hat by name ${hatType}`);
        }
        return clone(hat) as Mesh;
    }

    protected resolveHatType(): HatType {
        if (this.health == 200) {
            return HatType.Helmet;
        }
        return null;
    }

    protected async dropHat(): Promise<void> {
        if (this.hat == null) {
            return;
        }

        const hat = this.hat;
        this.hat = null;

        await this.makeHatFlyOffHead(hat, this.head, this.parent);
        this.destroyHat(hat);
    }

    protected async makeHatFlyOffHead(hat: Object3D, head: Object3D, scene: Object3D, floorY: number = 0.2): Promise<void> {
        scene.attach(hat); // Detach hat from head and attach to scene

        const velocity = new Vector3((Math.random() - 0.5) * 0.1, 0.12 + Math.random() * 0.05, 0.03 + Math.random() * 0.05);
        const gravity = new Vector3(0, -0.01, 0);
        const rotationVelocity = new Vector3(Math.random() * 0.02, Math.random() * 0.03, Math.random() * 0.02).multiplyScalar(5);

        return new Promise<void>((resolve) => {
            const flyPhase = () => {
                velocity.add(gravity);
                hat.position.add(velocity);

                hat.rotation.x += rotationVelocity.x;
                hat.rotation.y += rotationVelocity.y;
                hat.rotation.z += rotationVelocity.z;

                if (hat.position.y < floorY) {
                    Ticker.shared.remove(flyPhase, this);
                    // Clamp to floor and zero Y velocity
                    hat.position.y = floorY;
                    velocity.y = 0;
                    resolve();
                }
            }
            Ticker.shared.add(flyPhase, this);
        });
    }

    protected destroyHat(object: Object3D) {
        object.traverse((child) => {
            if ((child as Mesh).geometry) {
                (child as Mesh).geometry.dispose();
            }

            if ((child as Mesh).material) {
                const material = (child as Mesh).material;
                if (Array.isArray(material)) {
                    material.forEach((mat) => mat.dispose());
                } else {
                    material.dispose();
                }
            }
        });

        if (object.parent) {
            object.parent.remove(object);
        }
    }

    protected randomIdleAnimationTimeout(min: number = 500, max: number = 3000, step: number = 500): number {
        const steps = Math.floor((max - min) / step) + 1;
        const randomIndex = Math.floor(Math.random() * steps);
        return min + randomIndex * step;
    }
}
